/**
 * Every dialog's primary action (TMM-B4): a contained button that shows progress while the action
 * runs (MUI `loading`), is disabled meanwhile so it cannot fire twice (PERF-006), and announces a
 * polite "Working…" to assistive technology (FEED-001). The live region is ALWAYS rendered, empty
 * while idle: screen readers (NVDA, JAWS) often skip a region inserted already filled, so only its
 * text changes. `color="error"` for a destructive action.
 */
import { Box, Button, type ButtonProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { visuallyHidden } from './visuallyHidden';

export interface SubmitButtonProps extends Omit<ButtonProps, 'variant'> {
  loading: boolean;
}

export function SubmitButton({
  loading,
  disabled,
  type = 'button',
  children,
  ...rest
}: SubmitButtonProps) {
  const { t } = useTranslation();
  return (
    <>
      <Button
        {...rest}
        variant="contained"
        type={type}
        loading={loading}
        disabled={disabled || loading}
      >
        {children}
      </Button>
      <Box component="span" role="status" aria-live="polite" sx={visuallyHidden}>
        {loading ? t('common.working') : ''}
      </Box>
    </>
  );
}
